"use client";

import React, { useState, useEffect } from "react";
import { Upload, FileAudio2, CloudUpload, X } from "lucide-react";
import { VALID_EXTENSIONS } from "./types";
import { UploadStatus } from "..";

type FileDropAreaProps = {
  isDragging: boolean;
  selectedFileName: string;
  uploadProgress: number;
  isSubmitting: boolean;
  status: UploadStatus;
  getUploadStatusStyles: () => string;
  isProcessing: (state: UploadStatus["state"]) => boolean;
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  openFileSelector: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileRemove: () => void;
};

export const FileDropArea: React.FC<FileDropAreaProps> = ({
  isDragging,
  selectedFileName,
  uploadProgress,
  isSubmitting,
  status,
  getUploadStatusStyles,
  isProcessing,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  openFileSelector,
  fileInputRef,
  handleFileChange,
  handleFileRemove
}) => {

    const [isMobile, setIsMobile] = useState(false);
  
    // モバイル判定のためのメディアクエリを監視
    useEffect(() => {
      const mediaQuery = window.matchMedia('(max-width: 767px)'); // md未満をモバイルとして判定
      
      // 初期値を設定
      setIsMobile(mediaQuery.matches);
      
      // メディアクエリの変更を監視
      const handleChange = (event: MediaQueryListEvent) => {
        setIsMobile(event.matches);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      
      // クリーンアップ
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }, []);
  
    // ヘルプテキストの内容を決定
    const getHelpText = () => {
      if (isDragging) {
        return "ファイルをドロップしてアップロード";
      }
      
      if (isMobile) {
        return "ファイルを選択";
      }
      
      return "ファイルをドラッグ&ドロップまたは、クリックして選択";
    };

    // 全体のクリックハンドラー
    const handleAreaClick = () => {
      if (!isSubmitting && !isProcessing(status.state)) {
        openFileSelector();
      }
    };
  
  return (
    <div className="mt-4">
      {/* ファイル入力要素 - フォーカス不可にしてアクセシビリティ問題を解決 */}
      <input
        id="file-upload"
        name="file"
        type="file"
        accept={VALID_EXTENSIONS.map(ext => `audio/${ext}`).join(',')}
        ref={(el) => {
          if (fileInputRef && el) {
            fileInputRef.current = el;
          }
        }}
        className="sr-only"
        onChange={handleFileChange}
        disabled={isSubmitting || isProcessing(status.state)}
        tabIndex={-1}
        aria-hidden="true"
      />
      <div 
        className={`upload-area mt-1 expanded-drop-area px-4 pt-5 pb-7 rounded-lg transition-all cursor-pointer relative ${isDragging ? 'pulse-animation' : ''} ${getUploadStatusStyles()}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleAreaClick}
        role="button"
        tabIndex={0}
        aria-label={`音声ファイルアップロード領域。${selectedFileName ? `選択中: ${selectedFileName}` : 'ファイルが選択されていません'}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAreaClick();
          }
        }}
      >
        {/* 削除ボタン - 右上に配置 */}
        {selectedFileName && !isDragging && !isSubmitting && !isProcessing(status.state) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleFileRemove();
            }}
            className="absolute top-2 right-2 pointer-events-auto p-1.5 hover:bg-gray-500/30 text-gray-500 hover:text-gray-700 transition-colors duration-200 z-10"
            aria-label="ファイルを削除"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="space-y-2 text-center pointer-events-none">
          <div className="flex flex-col items-center">
            {/* アイコン表示 */}
            {isDragging ? (
              <Upload 
                className="mx-auto h-16 w-16 text-primary animate-bounce" 
                strokeWidth={1.5}
                aria-hidden="true"
              />
            ) : selectedFileName ? (
              <FileAudio2 
                className="mx-auto h-16 w-16 text-primary" 
                strokeWidth={1.5}
                aria-hidden="true"
              />
            ) : (
              <CloudUpload 
                className="mx-auto h-14 w-14 text-gray-400 dark:text-gray-500 transition-all duration-200 group-hover:text-primary/70" 
                strokeWidth={1.5}
                aria-hidden="true"
              />
            )}
            
            {/* ファイル選択テキスト */}
            <div className={`flex text-sm mt-4 ${isDragging ? 'text-primary font-bold' : selectedFileName ? 'text-primary font-bold' : 'text-foreground/80'}`}>
              <span className={isDragging ? 'text-xl animate-pulse' : selectedFileName ? 'text-md' : 'text-lg'}>
                {isDragging 
                  ? "ここにドロップ！" 
                  : selectedFileName 
                    ? selectedFileName 
                    : "ファイルを選択"}
              </span>
            </div>
            
            {/* アップロードプログレス */}
            {selectedFileName && (
              <div className="w-full mt-3 px-4">
                {uploadProgress < 100 && (
                  <div className="w-full h-2 bg-gray-200/30 rounded-full overflow-hidden" role="progressbar" aria-valuenow={Math.floor(uploadProgress)} aria-valuemin={0} aria-valuemax={100}>
                    <div 
                      className="bg-black dark:bg-white h-full rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1" aria-live="polite">
                  {uploadProgress < 100 ? `アップロード中... ${Math.floor(uploadProgress)}%` : 'ファイルセット完了'}
                </p>
              </div>
            )}
            
            {/* ヘルプテキスト */}
            {!selectedFileName && (
              <>
                {/* モバイルではヘルプテキストを非表示 */}
                {!isMobile && (
                  <p className={`text-sm ${isDragging ? 'text-primary font-semibold' : 'text-gray-500'} mt-3`}>
                    {getHelpText()}
                  </p>
                )}
                <p id="file-help" className="text-xs text-gray-500 mt-2">
                  {VALID_EXTENSIONS.join(', ').toUpperCase()}（最大500MB程度）
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};